import { FormEvent, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { trackPageView } from '../utils/analytics'
import {
  deleteAccount,
  getDeletionErrorMessage,
  normalizeEmail,
  requestCode,
  verifyCodeForIdToken,
} from '../services/accountDeletionApi'

type Step = 'email' | 'code' | 'confirm' | 'done'

const inputClass = 'w-full border rounded px-3 py-2 text-black'
const primaryButton = 'px-4 py-2 rounded bg-red-600 text-white font-medium disabled:opacity-50'
const linkButton = 'underline text-sm disabled:opacity-50'

/**
 * Web account deletion page. Google Play requires a URL where people can ask to
 * delete their account without the app installed; this is that URL
 * (https://zwizle.com/delete-account). Ownership is proved with an emailed
 * code, then the person confirms once more before anything is deleted.
 * Flow and rationale: see accountDeletionApi.ts.
 */
export const DeleteAccountPage = () => {
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [idToken, setIdToken] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // State is stale within one tick, so a ref stops a double click sending twice.
  const inFlight = useRef(false)

  useEffect(() => {
    try {
      trackPageView({ page: '/delete-account' })
    } catch (err) {
      console.error('Failed to track delete account page view:', err)
    }
  }, [])

  const run = async (action: () => Promise<void>) => {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(getDeletionErrorMessage(err))
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const sendCode = () =>
    run(async () => {
      await requestCode(email)
      setCode('')
      setStep('code')
    })

  const onSubmitEmail = (event: FormEvent) => {
    event.preventDefault()
    void sendCode()
  }

  const onSubmitCode = (event: FormEvent) => {
    event.preventDefault()
    void run(async () => {
      setIdToken(await verifyCodeForIdToken(email, code))
      setStep('confirm')
    })
  }

  const onConfirmDelete = () =>
    void run(async () => {
      if (!idToken) return
      await deleteAccount(idToken)
      setIdToken(null)
      setStep('done')
    })

  return (
    <div className="max-w-xl mx-auto px-4 py-8 text-left">
      <h1 className="text-2xl font-bold mb-2">Delete your Zwizle account</h1>
      <p className="mb-4 leading-relaxed">
        Use this page to permanently delete your Zwizle account without the app. You can also do it in
        the app: Profile, then Delete account.
      </p>

      <section aria-labelledby="delete-steps" className="mb-6">
        <h2 id="delete-steps" className="text-lg font-semibold mb-1">
          How to delete your account
        </h2>
        <ol className="list-decimal pl-6 space-y-1">
          <li>Enter the email address of your Zwizle account below.</li>
          <li>Enter the 6-digit code we email to you (it expires in 10 minutes).</li>
          <li>Confirm the deletion. It happens immediately and cannot be undone.</li>
        </ol>
      </section>

      <section aria-labelledby="delete-data" className="mb-8 leading-relaxed">
        <h2 id="delete-data" className="text-lg font-semibold mb-1">
          What we delete and what we keep
        </h2>
        <p className="mt-1">Deleted immediately:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Your Zwizle sign-in account (Google, Apple or email code) and its account identifier.</li>
          <li>The email address linked to it, and any sign-in codes still pending for it.</li>
        </ul>
        <p className="mt-2">Kept, and why:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Leaderboard entries and game sessions that are tied only to an anonymous device ID, not to
            your account. Uninstalling the app or clearing your browser's site data removes the ID from
            your device.
          </li>
          <li>Aggregated or anonymized analytics that no longer identify you.</li>
          <li>
            Short-lived security records: sign-in rate-limit records expire automatically within an hour,
            and security logs (which hold only one-way hashes, not your email address) are deleted after
            30 days.
          </li>
          <li>Anything we are legally required to keep.</li>
        </ul>
        <p className="mt-2">
          Full details are in the{' '}
          <Link className="underline" to="/privacy#account-deletion">
            privacy policy
          </Link>
          . Questions? Email{' '}
          <a className="underline" href="mailto:privacy@zwizle.com">
            privacy@zwizle.com
          </a>
          .
        </p>
      </section>

      {error && (
        <p role="alert" data-testid="delete-error" className="mb-4 text-red-600">
          {error}
        </p>
      )}

      {step === 'email' && (
        <form onSubmit={onSubmitEmail} className="space-y-4">
          <label className="block">
            <span className="block mb-1">Email address of your account</span>
            <input
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </label>
          <p className="text-sm text-gray-500">We will email you a 6-digit code to confirm the account is yours.</p>
          <button type="submit" disabled={busy || !normalizeEmail(email)} className={primaryButton}>
            Send code
          </button>
        </form>
      )}

      {step === 'code' && (
        <form onSubmit={onSubmitCode} className="space-y-4">
          <p>
            If an account exists for <strong>{normalizeEmail(email)}</strong>, a code is on its way. It
            expires in 10 minutes.
          </p>
          <label className="block">
            <span className="block mb-1">6-digit code</span>
            <input
              type="text"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className={inputClass}
            />
          </label>
          <div className="flex items-center gap-4">
            <button type="submit" disabled={busy || code.length !== 6} className={primaryButton}>
              Verify code
            </button>
            <button type="button" disabled={busy} onClick={() => void sendCode()} className={linkButton}>
              Send a new code
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setError(null)
                setStep('email')
              }}
              className={linkButton}>
              Change email
            </button>
          </div>
        </form>
      )}

      {step === 'confirm' && (
        <div className="space-y-4">
          <p>
            Email verified. This permanently deletes the Zwizle account for{' '}
            <strong>{normalizeEmail(email)}</strong> and cannot be undone. Game progress saved on your
            devices is not affected.
          </p>
          <button type="button" disabled={busy} onClick={onConfirmDelete} className={primaryButton}>
            Permanently delete my account
          </button>
        </div>
      )}

      {step === 'done' && (
        <p data-testid="delete-done" role="status">
          Your account has been deleted. You are welcome to play Zwizle as a guest at any time.
        </p>
      )}
    </div>
  )
}
